import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CalendarDays, Plus, ChevronLeft, ChevronRight, Clock, MapPin,
  Wrench, User, Trash2, Pencil, ExternalLink, Users, CalendarPlus, Inbox
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { contactPrimaryLabel } from '@/lib/contactDisplay';
import { jobCategoryLabel } from '@/constants/jobCategories';

const EVENT_TYPE_VALUES = ['job', 'appointment', 'maintenance', 'personal', 'other'];
const EVENT_COLORS = { job: '#3399FF', appointment: '#10B981', maintenance: '#F59E0B', personal: '#8B5CF6', other: '#6B7280' };

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfMonth(year, month) { return new Date(year, month, 1).getDay(); }
function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}
function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hour12: true });
}

const ROLE_COLORS = {
  admin: '#003366',
  superadmin: '#003366',
  office: '#10B981',
  manager: '#8B5CF6',
  driller: '#F59E0B',
  pump_installer: '#EF4444',
  operator: '#6B7280',
};

export default function CalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', type: 'appointment', start_datetime: '', end_datetime: '', all_day: false, color: '#3399FF', notes: '', assigned_to: [] });

  // Team view data
  const [teamData, setTeamData] = useState([]);

  // TEMA C2 (Alian, "que se queden todos los trabajos"): jobs without a
  // scheduled_date have nowhere to sit on the calendar and used to just
  // vanish — this side lane keeps them visible + one click to schedule.
  const [unscheduledJobs, setUnscheduledJobs] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [schedulingJob, setSchedulingJob] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ date: '', time: '08:00' });

  // Alian (video D): hover over an event shows work/client/address/time/
  // assignee without a click — fixed-position so it escapes the grid's
  // overflow-hidden cells instead of getting clipped.
  const [hoverEvent, setHoverEvent] = useState(null); // { ev, x, y }
  const [contactsById, setContactsById] = useState({});

  useEffect(() => {
    api.get('/contacts', { params: { page_size: 500 } }).then(res => {
      const contacts = res.data.items || res.data || [];
      const map = {};
      contacts.forEach(c => { map[c.id] = c; });
      setContactsById(map);
    }).catch(() => {});
  }, []);

  const usersById = useMemo(() => {
    const map = {};
    users.forEach(u => { map[u.id] = u; });
    return map;
  }, [users]);

  const showEventTooltip = (ev) => (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHoverEvent({ ev, x: r.left + r.width / 2, y: r.top });
  };
  const hideEventTooltip = () => setHoverEvent(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const fetchEvents = useCallback(async () => {
    try {
      const start = new Date(year, month, 1).toISOString();
      const end = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
      const params = { start, end };
      if (filterUser !== 'all') params.assigned_to = filterUser;

      const requests = [
        api.get('/scheduling/events', { params }),
        api.get('/auth/users').catch(() => ({ data: [] }))
      ];
      if (viewMode === 'team') {
        requests.push(api.get('/scheduling/team-events', { params: { start, end } }));
      }
      const results = await Promise.all(requests);
      setEvents(results[0].data);
      setUsers(results[1].data);
      if (viewMode === 'team' && results[2]) {
        setTeamData(results[2].data.team || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [year, month, filterUser, viewMode]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const fetchUnscheduledJobs = useCallback(async () => {
    try {
      const res = await api.get('/jobs', { params: { scheduled: 'false', page_size: 100 } });
      setUnscheduledJobs(res.data.items || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchUnscheduledJobs(); }, [fetchUnscheduledJobs]);

  const openScheduleJob = (job) => {
    setSchedulingJob(job);
    const d = selectedDate || new Date();
    setScheduleForm({ date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, time: '08:00' });
    setScheduleDialogOpen(true);
  };

  const handleScheduleJob = async (e) => {
    e.preventDefault();
    if (!schedulingJob || !scheduleForm.date) return;
    try {
      await api.put(`/jobs/${schedulingJob.id}`, {
        status: 'scheduled',
        scheduled_date: scheduleForm.date,
        scheduled_time: scheduleForm.time,
      });
      toast.success(t('calendar.jobScheduled', 'Trabajo programado'));
      setScheduleDialogOpen(false);
      setSchedulingJob(null);
      fetchUnscheduledJobs();
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('calendar.errorSaving'));
    }
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(new Date()); };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const prevMonthDays = getDaysInMonth(year, month - 1);
    const days = [];
    for (let i = firstDay - 1; i >= 0; i--) days.push({ day: prevMonthDays - i, currentMonth: false, date: new Date(year, month - 1, prevMonthDays - i) });
    for (let i = 1; i <= daysInMonth; i++) days.push({ day: i, currentMonth: true, date: new Date(year, month, i) });
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) days.push({ day: i, currentMonth: false, date: new Date(year, month + 1, i) });
    return days;
  }, [year, month]);

  const getEventsForDay = (date) => {
    const filtered = filterUser === 'all' ? events : events.filter(e => (e.assigned_to || []).includes(filterUser) || !e.assigned_to?.length);
    return filtered.filter(e => { const eDate = new Date(e.start_datetime); return isSameDay(eDate, date); });
  };

  const selectedDayEvents = getEventsForDay(selectedDate);

  const openCreate = (date) => {
    setEditing(null);
    const d = date || selectedDate;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T09:00`;
    const isoEnd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T10:00`;
    setForm({ title: '', type: 'appointment', start_datetime: iso, end_datetime: isoEnd, all_day: false, color: '#3399FF', notes: '', assigned_to: [] });
    setDialogOpen(true);
  };

  const openEdit = (ev) => {
    if (ev.source === 'job') { navigate(`/jobs/${ev.source_id}`); return; }
    setEditing(ev);
    setForm({
      title: ev.title, type: ev.type, start_datetime: ev.start_datetime?.slice(0, 16) || '',
      end_datetime: ev.end_datetime?.slice(0, 16) || '', all_day: ev.all_day || false,
      color: ev.color || '#3399FF', notes: ev.notes || '', assigned_to: ev.assigned_to || []
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error(t('calendar.eventTitle') + ' ' + t('errors.requiredField').toLowerCase()); return; }
    try {
      const payload = { ...form, start_datetime: new Date(form.start_datetime).toISOString(), end_datetime: form.end_datetime ? new Date(form.end_datetime).toISOString() : '' };
      if (editing) {
        await api.put(`/scheduling/events/${editing.id}`, payload);
        toast.success(t('calendar.eventUpdated'));
      } else {
        await api.post('/scheduling/events', payload);
        toast.success(t('calendar.eventCreated'));
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (err) { toast.error(err.response?.data?.detail || t('calendar.errorSaving')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/scheduling/events/${id}`); toast.success(t('calendar.eventDeleted')); fetchEvents(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  const today = new Date();

  const getWeekDays = () => {
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  };
  const weekDays = getWeekDays();
  const HOURS = Array.from({ length: 14 }, (_, i) => i + 6);

  const getEventsForHour = (date, hour) => {
    const filtered = filterUser === 'all' ? events : events.filter(e => (e.assigned_to || []).includes(filterUser) || !e.assigned_to?.length);
    return filtered.filter(ev => { const evDate = new Date(ev.start_datetime); return isSameDay(evDate, date) && evDate.getHours() === hour; });
  };

  const navigateWeek = (dir) => { const d = new Date(selectedDate); d.setDate(d.getDate() + (dir * 7)); setSelectedDate(d); setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1)); };
  const navigateDay = (dir) => { const d = new Date(selectedDate); d.setDate(d.getDate() + dir); setSelectedDate(d); setCurrentDate(new Date(d.getFullYear(), d.getMonth(), 1)); };

  // Team view: get week days centered on selected date
  const teamWeekDays = useMemo(() => {
    const start = new Date(selectedDate);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [selectedDate]);

  const getTeamEventsForDay = (userId, date) => {
    const member = teamData.find(m => m.user_id === userId);
    if (!member) return [];
    return member.events.filter(e => {
      const eDate = new Date(e.start_datetime);
      return isSameDay(eDate, date);
    });
  };

  // ── Drag & Drop (month view): drop an event on another day to reschedule.
  const [draggingEventId, setDraggingEventId] = useState(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);

  const handleDragStart = (ev) => (e) => {
    setDraggingEventId(ev.id);
    try { e.dataTransfer.setData('text/plain', ev.id); } catch { /* no-op */ }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (cellDate) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetKey(cellDate.toDateString());
  };

  const handleDragLeave = () => setDropTargetKey(null);

  const handleDrop = (cellDate) => async (e) => {
    e.preventDefault();
    setDropTargetKey(null);
    const eventId = e.dataTransfer.getData('text/plain') || draggingEventId;
    setDraggingEventId(null);
    if (!eventId) return;
    const ev = events.find(x => x.id === eventId);
    if (!ev) return;
    const original = new Date(ev.start_datetime);
    if (isSameDay(original, cellDate)) return; // no-op

    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const buildIso = (base, src) => {
      const out = new Date(base);
      out.setHours(src.getHours(), src.getMinutes(), src.getSeconds(), 0);
      return out.toISOString();
    };

    try {
      if (ev.source === 'job' && ev.source_id) {
        // Job: update scheduled_date (capacity rules enforced server-side).
        // Multi-day jobs keep their span: shift start AND end by the same delta.
        const jobPayload = { scheduled_date: ymd(cellDate) };
        if (ev.range_start && ev.range_end && ev.range_end !== ev.range_start) {
          const chipDay = new Date(`${ev.start_datetime.slice(0, 10)}T00:00:00`);
          const target = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
          const delta = Math.round((target - chipDay) / 86400000);
          const rs = new Date(`${ev.range_start}T00:00:00`);
          const re = new Date(`${ev.range_end}T00:00:00`);
          rs.setDate(rs.getDate() + delta);
          re.setDate(re.getDate() + delta);
          jobPayload.scheduled_date = ymd(rs);
          jobPayload.scheduled_end_date = ymd(re);
        }
        try {
          await api.put(`/jobs/${ev.source_id}`, jobPayload);
        } catch (err) {
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail?.requires_confirmation) {
            const ok = window.confirm(detail.message || t('calendar.confirmReschedule', 'Confirmar mover'));
            if (!ok) return;
            await api.put(`/jobs/${ev.source_id}`, { ...jobPayload, override_schedule_conflict: true });
          } else if (err.response?.status === 409) {
            toast.error(detail?.message || t('calendar.scheduleConflict', 'Conflicto de agenda'));
            return;
          } else {
            throw err;
          }
        }
      } else if (ev.source) {
        // Other auto-generated events: not editable from the calendar.
        toast.error(t('calendar.cannotMoveAutoEvent', 'Este evento no se puede mover desde el calendario'));
        return;
      } else {
        // Custom scheduling event: shift start/end keeping the time-of-day.
        const newStart = buildIso(cellDate, original);
        let newEnd;
        if (ev.end_datetime) {
          const originalEnd = new Date(ev.end_datetime);
          const deltaMs = originalEnd - original;
          newEnd = new Date(new Date(newStart).getTime() + deltaMs).toISOString();
        }
        const payload = { start_datetime: newStart };
        if (newEnd) payload.end_datetime = newEnd;
        await api.put(`/scheduling/events/${ev.id}`, payload);
      }
      toast.success(t('calendar.eventMoved', 'Evento movido'));
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('calendar.errorSaving'));
    }
  };

  return (
    <div data-testid="calendar-page" className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('calendar.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{events.length} {t('calendar.title').toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {['month', 'week', 'day', 'team'].map(v => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setViewMode(v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${viewMode === v ? 'bg-white text-[#003366] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >{v === 'team' ? (
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{t('calendar.team', 'Equipo')}</span>
              ) : t(`calendar.${v}`)}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={goToday} className="text-xs">{t('calendar.today')}</Button>
          {viewMode !== 'team' && users.length > 0 && (
            <Select value={filterUser} onValueChange={setFilterUser}>
              <SelectTrigger data-testid="cal-filter-user" className="h-8 w-36 text-xs"><SelectValue placeholder={t('calendar.allUsers')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('calendar.allUsers')}</SelectItem>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button data-testid="new-event-btn" onClick={() => openCreate()} size="sm" className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm">
            <Plus className="w-4 h-4 mr-2" /> {t('calendar.newEvent')}
          </Button>
        </div>
      </div>

      {viewMode === 'team' ? (
        /* TEAM VIEW */
        <Card className="bg-white border-gray-100 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={() => navigateWeek(-1)}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-lg font-bold font-['Barlow'] text-gray-900">
                {teamWeekDays[0].getDate()} - {teamWeekDays[6].getDate()} {t(`calendar.months.${selectedDate.getMonth()}`)} {selectedDate.getFullYear()}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => navigateWeek(1)}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-2 overflow-x-auto">
            {teamData.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p>{t('calendar.noTeamEvents', 'No hay eventos de equipo esta semana')}</p>
              </div>
            ) : (
              <div className="min-w-[700px]">
                {/* Header: days of the week */}
                <div className="grid gap-px bg-gray-100 rounded-t-lg overflow-hidden" style={{ gridTemplateColumns: '160px repeat(7, 1fr)' }}>
                  <div className="bg-white p-2 text-xs font-semibold text-gray-500 uppercase">{t('calendar.teamMember', 'Miembro')}</div>
                  {teamWeekDays.map((d, i) => {
                    const isToday2 = isSameDay(d, today);
                    return (
                      <div key={i} className="bg-white text-center py-2">
                        <p className="text-[10px] uppercase text-gray-400 font-medium">{t(`calendar.days.${d.getDay()}`)}</p>
                        <p className={`text-sm font-bold mt-0.5 ${isToday2 ? 'bg-[#003366] text-white w-7 h-7 rounded-full inline-flex items-center justify-center' : 'text-gray-700'}`}>
                          {d.getDate()}
                        </p>
                      </div>
                    );
                  })}
                </div>
                {/* Rows: one per team member */}
                <div className="divide-y divide-gray-100">
                  {teamData.map((member) => (
                    <div key={member.user_id} className="grid gap-px bg-gray-50" style={{ gridTemplateColumns: '160px repeat(7, 1fr)' }}>
                      <div className="bg-white p-2 flex items-center gap-2 border-r border-gray-100">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: ROLE_COLORS[member.role] || '#6B7280' }}
                        >
                          {member.user_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">{member.user_name}</p>
                          <p className="text-[10px] text-gray-400 capitalize">{member.role}</p>
                        </div>
                      </div>
                      {teamWeekDays.map((d, di) => {
                        const dayEvents = getTeamEventsForDay(member.user_id, d);
                        return (
                          <div key={di} className="bg-white p-1 min-h-[60px] hover:bg-blue-50/30 transition-colors">
                            {dayEvents.map(ev => (
                              <div key={ev.id}
                                onClick={() => ev.source === 'job' ? navigate(`/jobs/${ev.source_id}`) : openEdit(ev)}
                                onMouseEnter={showEventTooltip(ev)}
                                onMouseLeave={hideEventTooltip}
                                className="text-[10px] px-1.5 py-1 rounded text-white font-medium mb-0.5 cursor-pointer truncate hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: ev.color || '#3399FF' }}
                              >
                                <div className="flex items-center gap-0.5">
                                  <Clock className="w-2.5 h-2.5 flex-shrink-0" />
                                  <span className="truncate">{formatTime(ev.start_datetime)}</span>
                                </div>
                                <span className="truncate block">{ev.title}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* STANDARD VIEWS (month, week, day) */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <Card className="lg:col-span-2 bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button data-testid="calendar-prev-month" variant="ghost" size="icon" onClick={viewMode === 'month' ? prevMonth : viewMode === 'week' ? () => navigateWeek(-1) : () => navigateDay(-1)}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="text-lg font-bold font-['Barlow'] text-gray-900">
                  {viewMode === 'day'
                    ? `${selectedDate.getDate()} ${t(`calendar.months.${selectedDate.getMonth()}`)} ${selectedDate.getFullYear()}`
                    : viewMode === 'week'
                      ? `${weekDays[0].getDate()} - ${weekDays[6].getDate()} ${t(`calendar.months.${selectedDate.getMonth()}`)} ${selectedDate.getFullYear()}`
                      : `${t(`calendar.months.${month}`)} ${year}`
                  }
                </h2>
                <Button data-testid="calendar-next-month" variant="ghost" size="icon" onClick={viewMode === 'month' ? nextMonth : viewMode === 'week' ? () => navigateWeek(1) : () => navigateDay(1)}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              {viewMode === 'month' && (
                <>
                  <div className="grid grid-cols-7 mb-2">
                    {[0,1,2,3,4,5,6].map(i => (
                      <div key={i} className="text-center text-xs font-semibold text-gray-400 uppercase tracking-wider py-2">{t(`calendar.days.${i}`)}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-px bg-gray-100 rounded-lg overflow-hidden">
                    {calendarDays.map((cell, i) => {
                      const dayEvents = getEventsForDay(cell.date);
                      const isToday = isSameDay(cell.date, today);
                      const isSelected = isSameDay(cell.date, selectedDate);
                      const isDropTarget = dropTargetKey === cell.date.toDateString();
                      return (
                        <div key={i} data-testid={`cal-day-${cell.day}-${cell.currentMonth}`}
                          onClick={() => setSelectedDate(cell.date)}
                          onDragOver={handleDragOver(cell.date)}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop(cell.date)}
                          className={`relative bg-white p-1.5 min-h-[88px] text-left transition-all hover:bg-blue-50/50 focus:outline-none cursor-pointer ${!cell.currentMonth ? 'opacity-40' : ''} ${isSelected ? 'ring-2 ring-[#3399FF] ring-inset z-10' : ''} ${isDropTarget ? 'ring-2 ring-emerald-400 ring-inset bg-emerald-50 z-20' : ''}`}
                        >
                          <span className={`text-xs font-medium inline-flex items-center justify-center w-6 h-6 rounded-full ${isToday ? 'bg-[#003366] text-white' : 'text-gray-700'}`}>
                            {cell.day}
                          </span>
                          <div className="flex flex-col gap-0.5 mt-1">
                            {dayEvents.slice(0, 3).map((ev) => {
                              const movable = !ev.source || ev.source === 'job';
                              return (
                                <div
                                  key={ev.id}
                                  data-testid={`cal-chip-${ev.id}`}
                                  draggable={movable}
                                  onDragStart={movable ? handleDragStart(ev) : undefined}
                                  onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                                  onMouseEnter={showEventTooltip(ev)}
                                  onMouseLeave={hideEventTooltip}
                                  className={`text-[10px] leading-tight px-1 py-0.5 rounded text-white truncate font-medium ${movable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${draggingEventId === ev.id ? 'opacity-50' : ''}`}
                                  style={{ backgroundColor: ev.color || '#3399FF' }}
                                >
                                  {ev.title}
                                </div>
                              );
                            })}
                            {dayEvents.length > 3 && <span className="text-[9px] text-gray-400">+{dayEvents.length - 3}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {viewMode === 'week' && (
                <div className="overflow-x-auto">
                  <div className="min-w-[600px]">
                    <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-100">
                      <div />
                      {weekDays.map((d, i) => {
                        const isToday2 = isSameDay(d, today);
                        return (
                          <div key={i} onClick={() => { setSelectedDate(d); setViewMode('day'); }} className="text-center py-2 cursor-pointer hover:bg-blue-50/50 transition-colors">
                            <p className="text-[10px] uppercase text-gray-400 font-medium">{t(`calendar.days.${i}`)}</p>
                            <p className={`text-sm font-bold mt-0.5 ${isToday2 ? 'bg-[#003366] text-white w-7 h-7 rounded-full inline-flex items-center justify-center' : 'text-gray-700'}`}>
                              {d.getDate()}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                      {HOURS.map(hour => (
                        <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-50 min-h-[48px]">
                          <div className="text-[10px] text-gray-400 font-mono text-right pr-2 pt-1">{hour}:00</div>
                          {weekDays.map((d, di) => {
                            const hEvents = getEventsForHour(d, hour);
                            return (
                              <div key={di} onClick={() => { setSelectedDate(d); openCreate(d); }} className="border-l border-gray-50 hover:bg-blue-50/30 transition-colors cursor-pointer p-0.5">
                                {hEvents.map(ev => (
                                  <div key={ev.id} onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                                    onMouseEnter={showEventTooltip(ev)}
                                    onMouseLeave={hideEventTooltip}
                                    className="text-[10px] px-1.5 py-0.5 rounded truncate text-white font-medium mb-0.5 cursor-pointer"
                                    style={{ backgroundColor: ev.color || '#3399FF' }}
                                  >{ev.title}</div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {viewMode === 'day' && (
                <div className="max-h-[480px] overflow-y-auto">
                  {HOURS.map(hour => {
                    const hEvents = getEventsForHour(selectedDate, hour);
                    return (
                      <div key={hour} className="flex border-b border-gray-50 min-h-[52px] hover:bg-blue-50/30 transition-colors cursor-pointer" onClick={() => openCreate(selectedDate)}>
                        <div className="w-16 text-xs text-gray-400 font-mono text-right pr-3 pt-2 flex-shrink-0">{hour}:00</div>
                        <div className="flex-1 p-1 border-l border-gray-100">
                          {hEvents.map(ev => (
                            <div key={ev.id} onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                              onMouseEnter={showEventTooltip(ev)}
                              onMouseLeave={hideEventTooltip}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white font-medium mb-1 cursor-pointer hover:opacity-90 transition-opacity"
                              style={{ backgroundColor: ev.color || '#3399FF' }}
                            >
                              <Clock className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{ev.title}</span>
                              <span className="text-xs opacity-80 ml-auto">{formatTime(ev.start_datetime)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold font-['Barlow']">
                  {selectedDate.getDate()} {t(`calendar.months.${selectedDate.getMonth()}`)}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => openCreate(selectedDate)} className="text-xs text-[#3399FF]">
                  <Plus className="w-3 h-3 mr-1" /> {t('common.create')}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {selectedDayEvents.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-400">
                  <CalendarDays className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  {t('calendar.noEventsDay')}
                </div>
              ) : (
                <div className="space-y-2">
                  {selectedDayEvents.map(ev => {
                    const isAutoEvent = !!ev.source;
                    return (
                    <div key={ev.id} data-testid={`event-card-${ev.id}`} 
                      className={`p-3 rounded-lg border transition-colors group ${isAutoEvent ? 'border-blue-100 bg-blue-50/30 hover:bg-blue-50' : 'border-gray-100 hover:bg-gray-50'}`}
                      onClick={isAutoEvent && ev.source === 'job' ? () => navigate(`/jobs/${ev.source_id}`) : undefined}
                      onMouseEnter={showEventTooltip(ev)}
                      onMouseLeave={hideEventTooltip}
                      style={isAutoEvent ? { cursor: 'pointer' } : undefined}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-1 h-10 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: ev.color || '#3399FF' }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">{ev.title}</p>
                            {isAutoEvent && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 flex-shrink-0" data-testid={`event-source-badge-${ev.id}`}>
                                <Wrench className="w-2.5 h-2.5" />
                                {ev.job_number || 'Job'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(ev.start_datetime)}</span>
                            {ev.notes && isAutoEvent && (
                              <span className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3" />{ev.notes}</span>
                            )}
                            {!isAutoEvent && (
                              <span className="capitalize px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                {t(`calendar.eventTypes.${ev.type}`, { defaultValue: ev.type })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                          {isAutoEvent ? (
                            <span className="p-1 text-blue-400"><ExternalLink className="w-3 h-3" /></span>
                          ) : (
                            <>
                              <button onClick={() => openEdit(ev)} className="p-1 text-gray-400 hover:text-blue-500"><Pencil className="w-3 h-3" /></button>
                              <button onClick={() => handleDelete(ev.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Unscheduled Jobs lane (TEMA C2) — jobs with no scheduled_date have
          nowhere to sit on the grid; keep them visible here with one click
          to schedule instead of just disappearing. */}
      <Card data-testid="unscheduled-jobs-panel" className="bg-white border-gray-100 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold font-['Barlow'] flex items-center gap-2">
            <Inbox className="w-4 h-4 text-amber-500" />
            {t('calendar.unscheduledJobs', 'Unscheduled Jobs')}
            {unscheduledJobs.length > 0 && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{unscheduledJobs.length}</span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {unscheduledJobs.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">{t('calendar.noUnscheduledJobs', 'No unscheduled jobs — everything has a date.')}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {unscheduledJobs.map(job => (
                <div key={job.id} data-testid={`unscheduled-job-${job.id}`}
                  className="flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-xs"
                >
                  <div className="flex flex-col leading-tight">
                    <span className="font-semibold text-gray-800">{job.job_number || job.title}</span>
                    <span className="text-[10px] text-gray-500">{jobCategoryLabel(job.type)}{job.well_address ? ` · ${job.well_address}` : ''}</span>
                  </div>
                  <Button
                    data-testid={`schedule-job-btn-${job.id}`}
                    size="sm" variant="ghost"
                    className="h-7 px-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                    onClick={() => openScheduleJob(job)}
                  >
                    <CalendarPlus className="w-3.5 h-3.5 mr-1" /> {t('calendar.scheduleJob', 'Schedule')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-4">
        {EVENT_TYPE_VALUES.map(v => (
          <div key={v} className="flex items-center gap-1.5 text-xs text-gray-500">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: EVENT_COLORS[v] }} />
            {t(`calendar.eventTypes.${v}`)}
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('calendar.editEvent') : t('calendar.newEvent')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('calendar.eventTitle')} *</Label>
              <Input data-testid="event-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder={t('calendar.eventTitle')} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('common.filter')}</Label>
                <Select value={form.type} onValueChange={v => { setForm({...form, type: v, color: EVENT_COLORS[v] || '#3399FF'}); }}>
                  <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPE_VALUES.map(v => <SelectItem key={v} value={v}>{t(`calendar.eventTypes.${v}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('calendar.color')}</Label>
                <div className="flex gap-2 pt-1.5">
                  {['#3399FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map(c => (
                    <button key={c} type="button" onClick={() => setForm({...form, color: c})}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
            {users.length > 0 && (
              <div className="space-y-2">
                <Label>{t('calendar.assignTo', 'Asignar a')}</Label>
                <Select value={form.assigned_to?.[0] || ''} onValueChange={v => setForm({...form, assigned_to: v ? [v] : []})}>
                  <SelectTrigger><SelectValue placeholder={t('calendar.selectUser', 'Seleccionar usuario')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">{t('calendar.noAssignment', 'Sin asignar')}</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('calendar.startDate')}</Label>
                <Input data-testid="event-start" type="datetime-local" value={form.start_datetime} onChange={e => setForm({...form, start_datetime: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('calendar.endDate')}</Label>
                <Input data-testid="event-end" type="datetime-local" value={form.end_datetime} onChange={e => setForm({...form, end_datetime: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('timecards.notes')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-event-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editing ? t('calendar.saveChanges') : t('calendar.createEvent')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="schedule-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-lg">{t('calendar.scheduleJob', 'Schedule')} — {schedulingJob?.job_number || schedulingJob?.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleScheduleJob} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('calendar.startDate')}</Label>
                <Input data-testid="schedule-job-date" type="date" value={scheduleForm.date} onChange={e => setScheduleForm({ ...scheduleForm, date: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>{t('calendar.schedule', 'Time')}</Label>
                <Input data-testid="schedule-job-time" type="time" value={scheduleForm.time} onChange={e => setScheduleForm({ ...scheduleForm, time: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setScheduleDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="schedule-job-confirm" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {t('calendar.scheduleJob', 'Schedule')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {hoverEvent && (
        <div
          className="bw-calendar-tooltip"
          style={{ left: hoverEvent.x, top: hoverEvent.y, transform: 'translate(-50%, -100%) translateY(-8px)' }}
        >
          {(() => {
            // Fix C (2026-08-31): un solo formato para TODOS los eventos, vengan
            // de un trabajo (source: 'job') o sean manuales (scheduling_events).
            // Alian: "no quiero tener este mismo tan largo... los otros no. Un
            // solo formato para no verlo diferente." Mismos campos, mismo orden;
            // el que no exista se omite (no vacio, no guion). Ancho fijo +
            // ellipsis (CSS) evita que unos se vean largos y otros cortos.
            const ev = hoverEvent.ev;
            const docNumber = ev.job_number || ev.estimate_number || ev.invoice_number || '';
            const typeLabel = ev.job_type ? jobCategoryLabel(ev.job_type) : (ev.type ? ev.type.charAt(0).toUpperCase() + ev.type.slice(1) : '');
            const headline = [docNumber, typeLabel].filter(Boolean).join(' · ') || ev.title;
            const client = ev.customer_id && contactsById[ev.customer_id] ? contactPrimaryLabel(contactsById[ev.customer_id]) : '';
            const address = ev.location?.address || (ev.source ? ev.notes : '') || '';
            const assignedNames = (ev.assigned_to || []).map(uid => usersById[uid]?.name).filter(Boolean);
            const time = ev.all_day
              ? t('calendar.allDay', 'Todo el día')
              : `${formatTime(ev.start_datetime)}${ev.end_datetime ? ' - ' + formatTime(ev.end_datetime) : ''}`;
            return (
              <>
                <p className="font-semibold text-white">{headline}</p>
                <div className="mt-1 space-y-0.5 text-slate-200">
                  {client && <p><span className="text-slate-400">{t('common.customer', 'Cliente')}:</span> {client}</p>}
                  {address && <p><span className="text-slate-400">{t('common.address', 'Dirección')}:</span> {address}</p>}
                  <p><span className="text-slate-400">{t('calendar.schedule', 'Horario')}:</span> {time}</p>
                  {assignedNames.length > 0 && (
                    <p><span className="text-slate-400">{t('calendar.assignTo', 'Asignado a')}:</span> {assignedNames.join(', ')}</p>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
